import React, {useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {
  designSystemToken,
  Icon,
  IconName,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import truncateText from '@/utils/truncateText';
import {YOUTUBE_DS_ICON_OVERRIDES} from '@/config/constants';
import {
  SingleUserInput,
  AudienceAgeSelection,
  BaseFilterOption,
  Filter,
  MultiSelection,
  MultiSelectionAsync,
  DynamicPlatformFilters,
  Range,
  RangeSelection,
  SingleSelection,
  WeightedFilterOption,
  WeightedSingleSelection,
  MultiUserInput,
} from '@/types/models/creator-crm/filter';
import {
  FilterIds,
  FiltersGroupId,
  PlatformId,
  PlatformFilterIds,
} from '@/types/models/creator-crm/filterId';
import {LdaUpdate, WeightUpdate} from '@/types/models/creator-crm/filterUpdate';
import AccordionList from '@/views/creators/creator-crm/components/actions-drawer/accordion-list';
import {ActionProps} from '@/views/creators/creator-crm/components/actions-drawer/actions/ActionProps';
import Filters from '@/views/creators/creator-crm/components/actions-drawer/filters';
import {platformIdToPlatformIconProps} from '@/views/creators/creator-crm/utils/platformIconUtils';
import searchCreatorsGetters from '@/views/creators/creator-crm/utils/searchCreatorsGetters';
import useDebouncedQuery from '@/hooks/use-debounced-query';
import useCreatorCrmStore, {
  creatorCrmActions,
} from '@/stores/creator-crm/creatorCrmStore';
import {shouldShowFilter} from '@/stores/creator-crm/utils/filterVisibilityUtils';
import styles from './Actions.module.scss';

const MAX_SUBTITLE_TEXT_LENGTH = 20;

const DEFAULT_APPLY_DEBOUNCE_MS = 500;

let applyTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Runs a filter change then applies it after an optional debounce (Creator CRM).
 * Use debounceMs=0 for instant apply (e.g. discrete choices); default 500ms for typing.
 * Does not close the filters drawer.
 */
function withImmediateApply(
  fn: () => void,
  debounceMs: number = DEFAULT_APPLY_DEBOUNCE_MS
) {
  fn();
  const doApply = () =>
    creatorCrmActions.applyFiltersGroup({closeDrawer: false});
  if (debounceMs === 0) {
    doApply();
    return;
  }
  if (applyTimeoutId) clearTimeout(applyTimeoutId);
  applyTimeoutId = setTimeout(() => {
    applyTimeoutId = null;
    doApply();
  }, debounceMs);
}

type FilterProps = {
  filtersGroupId: FiltersGroupId;
  filterId: FilterIds;
  filterValue: Filter;
  platformFilterIds?: PlatformFilterIds;
};

function SingleSelectionOptionsSegmentedControl({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as SingleSelection<BaseFilterOption>;
  return (
    <Filters.SingleSelectionOptionsSegmentedControl
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      options={castedFilterValue.options}
      selectedOption={castedFilterValue.selected}
    />
  );
}

function SingleSelectionOptionsDropDown({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as SingleSelection<BaseFilterOption>;
  return (
    <Filters.SingleSelectionOptionsDropDown
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      options={castedFilterValue.options}
      selectedOption={castedFilterValue.selected}
      placeholder={
        castedFilterValue.placeholderLocaleLabelKey
          ? translate(castedFilterValue.placeholderLocaleLabelKey)
          : undefined
      }
    />
  );
}

function WeightedSingleSelectionOptionsSegmentedControl({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue =
    filterValue as WeightedSingleSelection<WeightedFilterOption>;
  const selectedWeight = Object.keys(castedFilterValue.weights).find((key) => {
    return (
      castedFilterValue.weights[key] === castedFilterValue.selected?.weight
    );
  });
  return (
    <Filters.WeightedSingleSelectionOptionsSegmentedControl
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      weights={Object.keys(castedFilterValue.weights)}
      onUpdateWeight={(optionId: string, weightId: string) =>
        withImmediateApply(() => {
          const weightUpdate: WeightUpdate = {
            updateType: 'weight',
            optionId,
            weight: weightId,
          };
          creatorCrmActions.updateFilterOption(
            castedFilterValue.type,
            filtersGroupId,
            filterId,
            weightUpdate
          );
        }, 0)
      }
      options={castedFilterValue.options}
      selectedOption={castedFilterValue.selected}
      selectedWeightId={selectedWeight}
      weightPlaceholder={
        castedFilterValue.weightPlaceholderLocaleLabelKey
          ? translate(castedFilterValue.weightPlaceholderLocaleLabelKey)
          : undefined
      }
      weightDisabled={!castedFilterValue.selected?.value}
    />
  );
}

function AudienceAge({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as AudienceAgeSelection;
  return (
    <Filters.AudienceAge
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      weights={castedFilterValue.weights}
      onUpdateWeight={(optionId: string, weightId: string) =>
        withImmediateApply(() => {
          const weightUpdate: WeightUpdate = {
            updateType: 'weight',
            optionId,
            weight: weightId,
          };
          creatorCrmActions.updateFilterOption(
            castedFilterValue.type,
            filtersGroupId,
            filterId,
            weightUpdate
          );
        }, 0)
      }
      onUpdateLda={(checked: boolean) =>
        withImmediateApply(() => {
          const ldaUpdate: LdaUpdate = {
            updateType: 'lda',
            isLdaSelected: checked,
          };
          creatorCrmActions.updateFilterOption(
            castedFilterValue.type,
            filtersGroupId,
            filterId,
            ldaUpdate
          );
        }, 0)
      }
      options={castedFilterValue.options}
      selectedOptions={castedFilterValue.selected}
      weightPlaceholder={
        castedFilterValue.weightPlaceholderLocaleLabelKey
          ? translate(castedFilterValue.weightPlaceholderLocaleLabelKey)
          : undefined
      }
      isLdaSelected={castedFilterValue.ldaSelected}
      placeholder={
        castedFilterValue.placeholderLocaleLabelKey
          ? translate(castedFilterValue.placeholderLocaleLabelKey)
          : undefined
      }
    />
  );
}

function SingleSelectionOptionsGridWithIcon({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as SingleSelection<BaseFilterOption>;
  const options = castedFilterValue.options.map((option) => ({
    ...option,
    ...platformIdToPlatformIconProps(option.id as PlatformId, 'large'),
  }));
  return (
    <Filters.SingleSelectionOptionsGridWithIcon
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      options={options}
      selectedOption={castedFilterValue.selected}
    />
  );
}

function SingleSelectionOptionsRadioButtonsListWithIcon({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as SingleSelection<BaseFilterOption>;
  const options = castedFilterValue.options.map((option) => ({
    ...option,
    ...platformIdToPlatformIconProps(option.id as PlatformId, 'large'),
  }));
  return (
    <Filters.SingleSelectionOptionsRadioButtonsListWithIcon
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      options={options}
      selectedOption={castedFilterValue.selected}
    />
  );
}

function MultiSelectionOptionsList({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as MultiSelection<BaseFilterOption>;
  return (
    <Filters.MultiSelectionOptionsList
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      options={castedFilterValue.options}
      selectedOptions={castedFilterValue.selected}
    />
  );
}

function MultiSelectionOptionsDropDown({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as MultiSelection<BaseFilterOption>;
  return (
    <Filters.MultiSelectionOptionsDropDown
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      options={castedFilterValue.options}
      selectedOptions={castedFilterValue.selected}
      placeholder={
        castedFilterValue.placeholderLocaleLabelKey
          ? translate(castedFilterValue.placeholderLocaleLabelKey)
          : undefined
      }
    />
  );
}

function useLocationQuery(
  getOptions: (query: string) => Promise<BaseFilterOption[]>
) {
  const [query, setQuery] = React.useState<string>('');

  const {data: options, isFetching} = useDebouncedQuery({
    queryKey: ['filters', 'useLocationQuery', query],
    queryFn: () => getOptions(query),
    enabled: !!query,
    delay: 500,
  });

  return {setQuery, options, isFetching};
}

function MultiSelectionOptionsAutocompleteAsync({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue =
    filterValue as MultiSelectionAsync<BaseFilterOption>;

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const {setQuery, options, isFetching} = useLocationQuery(
    castedFilterValue.getOptions
  );

  return (
    <Filters.MultiSelectionOptionsAutocompleteAsync
      onInputChange={onInputChange}
      loading={isFetching}
      onChange={(value: BaseFilterOption) =>
        withImmediateApply(
          () =>
            creatorCrmActions.changeFilterOptionSelection(
              castedFilterValue.type,
              filtersGroupId,
              filterId,
              value,
              platformFilterIds
            ),
          0
        )
      }
      options={options || castedFilterValue.selected}
      selectedOptions={castedFilterValue.selected}
      placeholder={
        castedFilterValue.placeholderLocaleLabelKey
          ? translate(castedFilterValue.placeholderLocaleLabelKey)
          : undefined
      }
      isOptionEqualToValue={castedFilterValue.isOptionEqualToValue}
    />
  );
}

function RangeSelectionSliderAndInput({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
  noInputs,
}: FilterProps & {noInputs?: boolean}) {
  const castedFilterValue = filterValue as RangeSelection;
  const selectedRange: [number | undefined, number | undefined] =
    useMemo(() => {
      return [castedFilterValue.selected?.min, castedFilterValue.selected?.max];
    }, [castedFilterValue.selected]);

  const handleRangeChange = ([rangeMin, rangeMax]: [
    number | undefined,
    number | undefined,
  ]) => {
    let newSelectedRange: Range | undefined;
    if (rangeMin !== undefined || rangeMax !== undefined) {
      newSelectedRange = {min: rangeMin, max: rangeMax} as Range;
    }
    withImmediateApply(() =>
      creatorCrmActions.changeFilterRange(
        filtersGroupId,
        filterId,
        newSelectedRange,
        platformFilterIds
      )
    );
  };

  return (
    <Filters.RangeSelectionSliderAndInput
      selectedRange={selectedRange}
      steps={castedFilterValue.steps}
      onChange={handleRangeChange}
      noInputs={noInputs}
    />
  );
}

function MultiUserInputWithChips({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as MultiUserInput;
  return (
    <Filters.MultiUserInputWithChips
      onChange={(input: string) =>
        withImmediateApply(() =>
          creatorCrmActions.changeUserInputFilter(
            filtersGroupId,
            filterId,
            input,
            platformFilterIds
          )
        )
      }
      inputs={castedFilterValue.inputs}
      placeholder={
        castedFilterValue.placeholderLocaleLabelKey
          ? translate(castedFilterValue.placeholderLocaleLabelKey)
          : undefined
      }
    />
  );
}

function UserInput({
  filtersGroupId,
  filterId,
  filterValue,
  platformFilterIds,
}: FilterProps) {
  const castedFilterValue = filterValue as SingleUserInput;
  return (
    <Filters.UserInput
      onChange={(input: string) =>
        withImmediateApply(() =>
          creatorCrmActions.changeSingleUserInputFilter(
            filtersGroupId,
            filterId,
            input,
            platformFilterIds
          )
        )
      }
      value={castedFilterValue.input}
      placeholder={
        castedFilterValue.placeholderLocaleLabelKey
          ? translate(castedFilterValue.placeholderLocaleLabelKey)
          : undefined
      }
    />
  );
}

function Actions(props: ActionProps) {
  const {filtersGroupId, isSearchMode} = props;

  const filtersGroup = useCreatorCrmStore(
    useShallow(
      (state) => state.content.filtersState.filtersGroups[filtersGroupId]
    )
  );

  const {filters} = filtersGroup;

  const getFilterSubtitle = (filterId: FilterIds) => {
    const selectionString = searchCreatorsGetters.getActiveSelectionString(
      filtersGroup,
      filterId
    );

    const selectionIcon = searchCreatorsGetters.getActiveSelectionIconName(
      filtersGroup,
      filterId
    );

    const subtitleText = truncateText(
      selectionString,
      MAX_SUBTITLE_TEXT_LENGTH
    );

    if (selectionIcon) {
      const {iconName, iconStyle} = platformIdToPlatformIconProps(
        selectionIcon as PlatformId,
        'large'
      );
      if (iconName) {
        return (
          <span className={styles.subtitleWithIconContainer}>
            <Icon
              name={iconName}
              style={iconStyle as React.CSSProperties}
              size="large"
              appearance="neutral"
              color={designSystemToken('semantic.fg.inverse-secondary')}
              {...(iconName.includes('YouTube')
                ? YOUTUBE_DS_ICON_OVERRIDES
                : {})}
            />
            {subtitleText}
          </span>
        );
      }
    }
    return subtitleText;
  };

  const getFilterComponent = (
    filterId: FilterIds,
    filterValue: Filter,
    platformFilterIds?: PlatformFilterIds
  ) => {
    const baseProps = {
      filtersGroupId,
      filterId,
      filterValue,
      platformFilterIds,
    };
    switch (filterValue.presentationType) {
      case 'singleSelectionOptionsSegmentedControl': {
        return <SingleSelectionOptionsSegmentedControl {...baseProps} />;
      }
      case 'weightedSingleSelectionOptionsSegmentedControl':
        return (
          <WeightedSingleSelectionOptionsSegmentedControl {...baseProps} />
        );
      case 'singleSelectionOptionsGridWithIcon':
        return <SingleSelectionOptionsGridWithIcon {...baseProps} />;
      case 'singleSelectionOptionsRadioButtonsListWithIcon':
        return (
          <SingleSelectionOptionsRadioButtonsListWithIcon {...baseProps} />
        );
      case 'singleSelectionOptionsDropDown':
        return <SingleSelectionOptionsDropDown {...baseProps} />;
      case 'multiSelectionOptionsList': {
        return <MultiSelectionOptionsList {...baseProps} />;
      }
      case 'multiSelectionOptionsDropDown':
        return <MultiSelectionOptionsDropDown {...baseProps} />;
      case 'multiSelectionOptionsAutocompleteAsync':
        return <MultiSelectionOptionsAutocompleteAsync {...baseProps} />;
      case 'rangeSelectionSlider':
        return <RangeSelectionSliderAndInput {...baseProps} noInputs />;
      case 'rangeSelectionSliderAndInput':
        return <RangeSelectionSliderAndInput {...baseProps} />;
      case 'multiUserInputWithChips':
        return <MultiUserInputWithChips {...baseProps} />;
      case 'audienceAge':
        return <AudienceAge {...baseProps} />;
      case 'singleUserInput':
        return <UserInput {...baseProps} />;
      case 'platformFiltersGroup': {
        const castedFilterValue = filterValue as DynamicPlatformFilters;
        const platformFilters = Object.entries(
          castedFilterValue.platformsFilters[castedFilterValue.platformId]
        ).map(([platformFilterId, platformFilterValue]) => ({
          id: platformFilterId,
          title: translate(platformFilterValue.localeLabelKey),
          Component: getFilterComponent(
            filterId,
            platformFilterValue,
            platformFilterId as PlatformFilterIds
          ),
        }));
        return (
          <Filters.PlatformFiltersGroup platformFilterProps={platformFilters} />
        );
      }

      default:
        return null;
    }
  };

  const filtersList = Object.entries(filters)
    .filter(([, filterValue]) => !filterValue.isIrrelevant)
    .filter(([filterId]) =>
      shouldShowFilter(filterId as FilterIds, isSearchMode)
    )
    .map(([filterId, filterValue]) => ({
      id: filterId,
      title: translate(filterValue.localeLabelKey),
      subtitle: getFilterSubtitle(filterId as FilterIds),
      expanded:
        !filtersGroup.isCollapsable ||
        filtersGroup?.expandedFilter === filterId,
      expandIcon: filtersGroup.isCollapsable ? 'default' : null,
      Component: getFilterComponent(filterId as FilterIds, filterValue),
    }));

  return (
    <div>
      <AccordionList
        items={filtersList}
        onAccordionChange={(id) =>
          creatorCrmActions.toggleExpandFilter(filtersGroupId, id as FilterIds)
        }
      />
    </div>
  );
}

export default Actions;
